<script setup lang="ts">
import "swiper/css";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/20/solid";
import type { Swiper as SwiperInstance } from "swiper/types";
import { Swiper, SwiperSlide } from "swiper/vue";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

import { useUser } from "@/composables/api/useUser";

import Button from "@/components/ui/Button.vue";

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { data: user } = useUser();

const AMOUNT_OF_SLIDES = 3;

const swiperInstance = ref<SwiperInstance | null>(null);
const activeIndex = ref(0);

const isFirstSlide = computed(() => activeIndex.value === 0);
const isLastSlide = computed(() => activeIndex.value === AMOUNT_OF_SLIDES - 1);

function handleNext() {
  if (!isLastSlide.value) {
    swiperInstance.value?.slideNext();
  } else {
    emit("close");
  }
}
</script>

<template>
  <div class="relative flex w-full flex-col items-center gap-4">
    <h1
      :class="
        twMerge(
          'text-galbani text-2xl font-semibold',
          user?.current_mechanic === 'lactel' && 'text-lactel-blue',
        )
      "
    >
      {{ $t(`dialog.photoTutorial.title.${activeIndex + 1}`) }}
    </h1>

    <p class="text-secondary min-h-28 text-base leading-tight font-medium">
      {{ $t(`dialog.photoTutorial.tutorials.${activeIndex + 1}`) }}
    </p>

    <div class="relative w-full px-4">
      <Swiper
        :slidesPerView="1"
        :centeredSlides="true"
        :spaceBetween="24"
        class="relative h-full w-full"
        @slideChange="activeIndex = $event.activeIndex"
        @swiper="swiperInstance = $event"
      >
        <SwiperSlide>
          <img
            src="/images/photo-tutorial/how-to-1.png"
            alt="Comparaison d’un ticket froissé illisible à gauche et d’un ticket bien plat et lisible à droite, avec indication de bon et mauvais exemple."
          />
        </SwiperSlide>

        <SwiperSlide>
          <img
            src="/images/photo-tutorial/how-to-2.png"
            alt="Représentation d’un ticket de caisse bien plat.Exemple incorrect à gauche d’un ticket partiellement coupé et flou, à comparer avec un ticket bien centré et lisible à droite."
          />
        </SwiperSlide>

        <SwiperSlide>
          <img
            src="/images/photo-tutorial/how-to-3.png"
            alt="Ticket à gauche avec des marques de stylo et éléments surlignés, comparé à un ticket propre et non annoté à droite."
          />
        </SwiperSlide>
      </Swiper>

      <button
        type="button"
        :class="
          twMerge(
            'disabled:text-secondary text-galbani absolute top-1/2 -left-3 z-[1] flex size-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-xl',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
        :disabled="isFirstSlide"
        @click="swiperInstance?.slidePrev()"
      >
        <ChevronLeftIcon class="size-10" aria-hidden="true" />
      </button>

      <button
        type="button"
        :class="
          twMerge(
            'disabled:text-secondary text-galbani absolute top-1/2 -right-3 z-[1] flex size-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-xl',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
        :disabled="isLastSlide"
        @click="swiperInstance?.slideNext()"
      >
        <ChevronRightIcon class="size-10" aria-hidden="true" />
      </button>
    </div>

    <div class="relative flex items-center justify-center gap-3">
      <button
        v-for="index in AMOUNT_OF_SLIDES"
        :key="index - 1"
        type="button"
        class="cursor-pointer"
        :disabled="activeIndex === index - 1"
        @click="swiperInstance?.slideTo(index - 1)"
      >
        <span
          class="block size-3 rounded-full"
          :class="
            twMerge(
              '',
              activeIndex === index - 1 && user?.current_mechanic !== 'lactel' && 'bg-galbani',
              activeIndex !== index - 1 && user?.current_mechanic !== 'lactel' && 'bg-galbani/10',
              activeIndex === index - 1 && user?.current_mechanic === 'lactel' && 'bg-lactel-blue',
              activeIndex !== index - 1 &&
                user?.current_mechanic === 'lactel' &&
                'bg-lactel-blue/10',
            )
          "
          aria-hidden="true"
        />
      </button>
    </div>
  </div>

  <Button variant="secondary" class="mx-auto mt-8" @click="handleNext()">
    <template v-if="!isLastSlide">
      {{ $t("dialog.photoTutorial.nextButton") }}
    </template>

    <template v-else>
      {{ $t("dialog.photoTutorial.takePictureButton") }}
    </template>
  </Button>
</template>
